import { useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'

import { knowledgeThreadsApi, KnowledgeThreadSourcePreviewsView } from '@/lib/api'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import {
  onKnowledgeThreadCanvasImages,
  subscribeToKnowledgeThread,
  unsubscribeFromKnowledgeThread,
} from '@/lib/services/websocket'

import { SourcePreviewData, SourcePreviewTurn, webUrlsWithoutImage } from './sourcePreviews'

/** A page turn as this hook reads it: its message token (`serverToken ?? id`) and web citations. */
export interface SourcePreviewHookTurn extends SourcePreviewTurn {
  id: string
  serverToken?: string
  role: string
  streaming?: boolean
  text?: string
}

/** The persisted message token of a turn; a local id (not a uuid) is never sent. */
export function sourcePreviewMessageToken(turn: { id: string; serverToken?: string }): string {
  return turn.serverToken ?? turn.id
}

const MESSAGE_TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CARDS_FENCE = '```fairgen-cards'
/** How long one message's pictures may stay pending; after it the card keeps its no-image look. */
export const CARD_IMAGES_PENDING_WINDOW_MS = 2 * 60_000

function needsRequest(turn: SourcePreviewHookTurn): boolean {
  const hasCards = Boolean(turn.text?.includes(CARDS_FENCE))
  return webUrlsWithoutImage(turn).length > 0
    || (hasCards && (turn.cardImages === undefined || turn.sourceImagesLoading === true))
}

function previewData(view: KnowledgeThreadSourcePreviewsView | undefined, isPending: boolean): SourcePreviewData {
  const images = new Map<string, string>()
  for (const preview of view?.previews ?? []) {
    if (typeof preview.url === 'string' && typeof preview.imageUrl === 'string') {
      images.set(preview.url, preview.imageUrl)
    }
  }
  const cardImages = Array.isArray(view?.cardImages) ? view.cardImages : undefined
  return { images, loading: cardImages !== undefined ? isPending : undefined, ...(cardImages ? { cardImages } : {}) }
}

/**
 * Web source thumbnails and saved canvas pictures, loaded after the answer:
 * each settled assistant message that still shows a web source without an
 * image, or a canvas whose pictures are not settled, is asked once per page.
 * While pictures are pending the page holds the thread room: the job's
 * `canvas-images` event, a reconnect or a stall asks the pending messages
 * again, within a bounded window. A failed request is asked again on the next
 * revalidation.
 */
export function useSourcePreviews(
  threadToken: string,
  turns: ReadonlyArray<SourcePreviewHookTurn>,
  pendingWindowMs: number = CARD_IMAGES_PENDING_WINDOW_MS,
): ReadonlyMap<string, SourcePreviewData> {
  /** Per mount: messages whose result can no longer change, by `thread/message`. */
  const settledRef = useRef(new Map<string, SourcePreviewData>())
  /** Per mount: when each message was first seen pending. */
  const pendingSinceRef = useRef(new Map<string, number>())

  const { tokens, unsavedCanvases, hasPendingCanvas } = useMemo(() => {
    const found = new Set<string>()
    /** Canvas replies with no saved pictures on the turn yet (a reply that just ended). */
    const unsaved: Array<string> = []
    let pending = false
    for (const turn of turns) {
      const messageToken = sourcePreviewMessageToken(turn)
      if (turn.role === 'assistant' && !turn.streaming && MESSAGE_TOKEN.test(messageToken) && needsRequest(turn)) {
        found.add(messageToken)
        if (turn.text?.includes(CARDS_FENCE) && turn.cardImages === undefined) {
          unsaved.push(messageToken)
        }
        pending ||= turn.sourceImagesLoading === true
      }
    }
    return { tokens: [...found].sort().join(','), unsavedCanvases: unsaved.sort().join(','), hasPendingCanvas: pending }
  }, [turns])

  const { data, mutate } = useSWR(
    threadToken && tokens ? ['thread-source-previews', threadToken, tokens] as const : null,
    async ([, thread, joined]): Promise<ReadonlyMap<string, SourcePreviewData>> => {
      const results = new Map<string, SourcePreviewData>()
      await Promise.all(joined.split(',').map(async (messageToken) => {
        const key = `${thread}/${messageToken}`
        const settled = settledRef.current.get(key)
        if (settled) {
          results.set(messageToken, settled)
          return
        }
        try {
          const view = await knowledgeThreadsApi.getSourcePreviews(thread, messageToken)
          const now = Date.now()
          const since = pendingSinceRef.current.get(key) ?? now
          const isPending = view?.cardImagesPending === true && now - since < pendingWindowMs
          if (isPending) {
            pendingSinceRef.current.set(key, since)
          }
          const result = previewData(view, isPending)
          if (!isPending) {
            settledRef.current.set(key, result)
          }
          results.set(messageToken, result)
        } catch {
          // A missing thumbnail is a normal outcome: the card keeps its no-image look.
          results.set(messageToken, { images: new Map(), loading: false })
        }
      }))
      return results
    },
    {
      // SWR's deep compare reads every Map as empty, so a refetched result would never re-render.
      compare: (previous, next) => previous === next,
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
  )

  const previews = useMemo(() => {
    const waiting = unsavedCanvases ? unsavedCanvases.split(',').filter((messageToken) => !data?.has(messageToken)) : []
    if (waiting.length === 0) {
      return data ?? EMPTY
    }
    const merged = new Map(data ?? EMPTY)
    for (const messageToken of waiting) {
      merged.set(messageToken, { images: new Map(), loading: true })
    }
    return merged
  }, [data, unsavedCanvases])

  const isWaiting = Boolean(threadToken) && (
    [...previews.values()].some((preview) => preview.loading === true) || (hasPendingCanvas && data === undefined)
  )
  const tokensRef = useRef(tokens)
  tokensRef.current = tokens
  const { notifyActivity } = useRealtimeRepair({ active: isWaiting, onRepair: () => void mutate() })

  useEffect(() => {
    if (!isWaiting) {
      return
    }
    subscribeToKnowledgeThread(threadToken)
    const off = onKnowledgeThreadCanvasImages(threadToken, (event) => {
      if (tokensRef.current.split(',').includes(event.messageToken)) {
        notifyActivity()
        void mutate()
      }
    })
    return () => {
      off()
      unsubscribeFromKnowledgeThread(threadToken)
    }
  }, [isWaiting, threadToken, mutate, notifyActivity])

  return previews
}

const EMPTY: ReadonlyMap<string, SourcePreviewData> = new Map()
